/* =====================================================================
   LOCALE — лёгкая i18n под LUMEN (RU / EN / DE / FR).

   Философия (взята из методологии «Бюро сортировки», но без её
   Python/YAML-пайплайна — он оверкилл для компактной аркады):
   локализация-как-код, язык-эталон, namespace-ключи, контекст в
   глоссарии, валидация покрытия.

   Две дорожки текста:
   1) UI-«хром» (меню/HUD/итоги/баннеры) — живёт ЦЕЛИКОМ в словарях ui
      по всем языкам, ключи типизированы (UIKey). Отсутствие ключа в
      en/de/fr = ошибка компиляции (PACKS-присвоение ниже). Это и есть
      валидатор покрытия UI — отдельный скрипт не нужен.
   2) Данные контента (имена/описания врагов, оружия, пассивок и т.д.) —
      RU остаётся источником правды в config.ts (имена рядом с числами
      баланса, нулевой churn). en/de/fr дают перевод по тем же ключам
      сущностей через data-словарь. Покрытие данных проверяет лёгкий
      скрипт tools/loc-coverage.mjs (config-ключи vs словари).
   ===================================================================== */

import { Meta } from '../meta';
import { ru } from './ru';
import { en } from './en';
import { de } from './de';
import { fr } from './fr';

export type Lang = 'ru' | 'en' | 'de' | 'fr';
export const LANGS: Lang[] = ['ru', 'en', 'de', 'fr'];
export const LANG_LABELS: Record<Lang, string> = { ru: 'RU', en: 'EN', de: 'DE', fr: 'FR' };
export const LANG_NAMES: Record<Lang, string> = {
  ru: 'Русский', en: 'English', de: 'Deutsch', fr: 'Français',
};

// UIKey — канонический набор UI-ключей, выводится из эталонного ru.ui.
export type UIKey = keyof typeof ru.ui;
type UIDict = Record<UIKey, string>;
// Данные: category -> entity key -> поля. Не типизируем ключи строго
// (ключи config не статический union) — покрытие ловит loc-coverage.mjs.
type DataDict = Record<string, Record<string, { name?: string; desc?: string; hint?: string }>>;
// Лор-фрагменты «Пепелище» (§14): пулы строк-голосов по категориям (смерть/имена забегов/
// святилища/победа). Свободные списки — не 1:1 между языками (выбор по индексу ВНУТРИ языка).
type LoreDict = Record<string, string[]>;
interface Pack { ui: UIDict; data: DataDict; lore?: LoreDict; }

// Присвоение типу Record<Lang, Pack> ФОРСИТ полноту ui во всех языках:
// если в en/de/fr.ui не хватает ключа из ru.ui — здесь ошибка сборки.
const PACKS: Record<Lang, Pack> = { ru, en, de, fr };

// ICONS — ЕДИНСТВЕННЫЙ источник правды для глифов-иконок UI. Раньше каждый глиф
// был физически продублирован в строках всех 4 языков; теперь locale-строки несут
// семантический плейсхолдер ({spark}/{gem}/…), а сам глиф живёт здесь один раз.
// Рескин (например ✦ → знак пламени) = правка одной строки тут, без обхода языков.
export const ICONS: Record<string, string> = {
  spark: '✦',  // свет / рассвет / вечная ночь / святилище / реликвия — брендовый знак
  gem:   '◆',  // золото / магазин
  bolt:  '⚡',  // вспышка силы (урон)
  play:  '▶',  // играть / продолжить / заново
  gear:  '⚙',  // настройки
  moon:  '☾',  // завет тьмы / аномалия
  star:  '★',  // эволюция / финал
  skull: '☠',  // убийства
  cycle: '↻',  // пересобрать драфт
};

// Один проход при загрузке модуля: разворачивает {icon}-плейсхолдеры в строках
// словарей. Делается ОДИН раз (не в interp на каждый t()), чтобы не смешивать
// namespace глифов (статичная конфигурация) с namespace runtime-параметров {n}/{kills}.
// Незнакомый {k} НЕ из ICONS остаётся нетронутым — его раскроет interp() из params.
(function expandPackIcons() {
  const sub = (s: string) => s.replace(/\{(\w+)\}/g, (m, k) => (k in ICONS ? ICONS[k] : m));
  for (const lang of LANGS) {
    const ui = PACKS[lang].ui as Record<string, string>;
    for (const key in ui) ui[key] = sub(ui[key]);
  }
})();

function interp(s: string, params?: Record<string, any>): string {
  if (!params) return s;
  return s.replace(/\{(\w+)\}/g, (m, k) => (k in params ? String(params[k]) : m));
}

function detect(): Lang {
  try {
    const navLang = (navigator.language || (navigator as any).userLanguage || 'en').toLowerCase();
    const code = navLang.slice(0, 2);
    if (code === 'ru') return 'ru';
    if (code === 'de') return 'de';
    if (code === 'fr') return 'fr';
  } catch (e) {}
  return 'en';   // дефолт-фолбэк для Steam/международной аудитории
}

export const Locale = {
  current: 'en' as Lang,

  // Инициализация на старте: сохранённый язык игрока, иначе детект браузера.
  init(saved?: string) {
    if (saved && (LANGS as string[]).includes(saved)) this.current = saved as Lang;
    else this.current = detect();
    this._syncHtml();
  },

  // Сменить язык явно (из переключателя) — персистится в Meta.
  set(lang: Lang) {
    this.current = lang;
    try { Meta.data.lang = lang; Meta.save(); } catch (e) {}
    this._syncHtml();
  },

  cycle() {
    const i = LANGS.indexOf(this.current);
    this.set(LANGS[(i + 1) % LANGS.length]);
  },

  // обновить <html lang> + статичную подсказку поворота устройства (вне canvas)
  _syncHtml() {
    try {
      document.documentElement.setAttribute('lang', this.current);
      const el = document.querySelector('#rotate .rotate-txt');
      if (el) el.innerHTML = t('html.rotate');
    } catch (e) {}
  },
};

// UI-строка по ключу + интерполяция {var}. Фолбэк: current -> en -> ключ.
export function t(key: UIKey, params?: Record<string, any>): string {
  const cur = PACKS[Locale.current];
  const s = (cur && cur.ui[key]) ?? PACKS.en.ui[key] ?? (key as string);
  return interp(s, params);
}

// Поле данных контента. ru -> fallback (значение из config). Иначе
// словарь языка -> en -> fallback. fallback всегда передаёт вызывающий
// (он уже читает config), поэтому дублирования RU-данных нет.
export function tField(cat: string, key: string | number, field: 'name' | 'desc' | 'hint', fallback: string): string {
  if (Locale.current === 'ru') return fallback;
  const k = String(key);
  const cur = PACKS[Locale.current].data[cat];
  const v = cur && cur[k] && cur[k][field];
  if (v) return v;
  const env = PACKS.en.data[cat] && PACKS.en.data[cat][k] && PACKS.en.data[cat][k][field];
  return env || fallback;
}

export function tName(cat: string, key: string | number, fallback: string): string {
  return tField(cat, key, 'name', fallback);
}
export function tDesc(cat: string, key: string | number, fallback: string): string {
  return tField(cat, key, 'desc', fallback);
}
export function tHint(key: string | number, fallback: string): string {
  return tField('unlocks', key, 'hint', fallback);
}

// Пул лор-фрагментов «Пепелище» по категории на текущем языке (фолбэк — en, затем пусто).
export function loreList(cat: string): string[] {
  const cur = PACKS[Locale.current].lore;
  const v = cur && cur[cat];
  if (v && v.length) return v;
  const env = PACKS.en.lore && PACKS.en.lore[cat];
  return env || [];
}
